import React from 'react';
import { Icon } from '@iconify/react';

interface FileTypeIconProps {
  fileName: string;
  size?: number;
}

/**
 * 文件扩展名 → vscode-icons 图标名称映射
 * 使用 VS Code 风格文件图标，在小尺寸列表中清晰美观
 */
const extensionIconMap: Record<string, string> = {
  // PDF
  pdf: 'vscode-icons:file-type-pdf2',

  // Office 文档
  doc: 'vscode-icons:file-type-word',
  docx: 'vscode-icons:file-type-word',
  rtf: 'vscode-icons:file-type-word',
  odt: 'vscode-icons:file-type-word2',
  xls: 'vscode-icons:file-type-excel',
  xlsx: 'vscode-icons:file-type-excel',
  ods: 'vscode-icons:file-type-excel2',
  csv: 'vscode-icons:file-type-excel',
  ppt: 'vscode-icons:file-type-powerpoint',
  pptx: 'vscode-icons:file-type-powerpoint',
  odp: 'vscode-icons:file-type-powerpoint2',

  // Markdown / 文本
  md: 'vscode-icons:file-type-markdown',
  markdown: 'vscode-icons:file-type-markdown',
  mdx: 'vscode-icons:file-type-markdown',
  txt: 'vscode-icons:file-type-text',
  log: 'vscode-icons:file-type-log',
  text: 'vscode-icons:file-type-text',

  // 图片
  jpg: 'vscode-icons:file-type-image',
  jpeg: 'vscode-icons:file-type-image',
  png: 'vscode-icons:file-type-image',
  gif: 'vscode-icons:file-type-image',
  svg: 'vscode-icons:file-type-svg',
  webp: 'vscode-icons:file-type-webp',
  ico: 'vscode-icons:file-type-image',
  bmp: 'vscode-icons:file-type-image',
  tiff: 'vscode-icons:file-type-image',
  tif: 'vscode-icons:file-type-image',
  heic: 'vscode-icons:file-type-image',
  avif: 'vscode-icons:file-type-avif',

  // 视频
  mp4: 'vscode-icons:file-type-video',
  avi: 'vscode-icons:file-type-video',
  mov: 'vscode-icons:file-type-video',
  wmv: 'vscode-icons:file-type-video',
  flv: 'vscode-icons:file-type-video',
  mkv: 'vscode-icons:file-type-video',
  webm: 'vscode-icons:file-type-video',

  // 音频
  mp3: 'vscode-icons:file-type-audio',
  wav: 'vscode-icons:file-type-audio',
  flac: 'vscode-icons:file-type-audio',
  aac: 'vscode-icons:file-type-audio',
  ogg: 'vscode-icons:file-type-audio',
  wma: 'vscode-icons:file-type-audio',
  m4a: 'vscode-icons:file-type-audio',

  // JavaScript / TypeScript
  js: 'vscode-icons:file-type-js',
  jsx: 'vscode-icons:file-type-reactjs',
  ts: 'vscode-icons:file-type-typescript',
  tsx: 'vscode-icons:file-type-reactts',

  // Python
  py: 'vscode-icons:file-type-python',

  // Java / JVM
  java: 'vscode-icons:file-type-java',
  kt: 'vscode-icons:file-type-kotlin',
  scala: 'vscode-icons:file-type-scala',

  // C / C++ / C#
  c: 'vscode-icons:file-type-c',
  cpp: 'vscode-icons:file-type-cpp',
  cs: 'vscode-icons:file-type-csharp',
  h: 'vscode-icons:file-type-cppheader',

  // Go / Rust / Swift / Dart
  go: 'vscode-icons:file-type-go',
  rs: 'vscode-icons:file-type-rust',
  swift: 'vscode-icons:file-type-swift',
  dart: 'vscode-icons:file-type-dartlang',

  // Ruby / PHP / Perl / Lua / R
  rb: 'vscode-icons:file-type-ruby',
  php: 'vscode-icons:file-type-php',
  pl: 'vscode-icons:file-type-perl',
  lua: 'vscode-icons:file-type-lua',
  r: 'vscode-icons:file-type-r',

  // Shell / Bat
  sh: 'vscode-icons:file-type-shell',
  bash: 'vscode-icons:file-type-shell',
  bat: 'vscode-icons:file-type-bat',
  ps1: 'vscode-icons:file-type-powershell',

  // Web 前端
  html: 'vscode-icons:file-type-html',
  htm: 'vscode-icons:file-type-html',
  css: 'vscode-icons:file-type-css',
  scss: 'vscode-icons:file-type-scss',
  sass: 'vscode-icons:file-type-sass',
  less: 'vscode-icons:file-type-less',
  vue: 'vscode-icons:file-type-vue',
  svelte: 'vscode-icons:file-type-svelte',

  // 数据 / 配置
  json: 'vscode-icons:file-type-json',
  jsonl: 'vscode-icons:file-type-json',
  json5: 'vscode-icons:file-type-json5',
  xml: 'vscode-icons:file-type-xml',
  yaml: 'vscode-icons:file-type-yaml',
  yml: 'vscode-icons:file-type-yaml',
  toml: 'vscode-icons:file-type-toml',
  ini: 'vscode-icons:file-type-ini',
  env: 'vscode-icons:file-type-dotenv',
  conf: 'vscode-icons:file-type-config',
  config: 'vscode-icons:file-type-config',

  // SAS
  sas: 'vscode-icons:file-type-sas',
  sas7bdat: 'vscode-icons:file-type-sas',

  // SQL
  sql: 'vscode-icons:file-type-sql',

  // 压缩 / 归档
  zip: 'vscode-icons:file-type-zip',
  rar: 'vscode-icons:file-type-zip',
  '7z': 'vscode-icons:file-type-zip',
  tar: 'vscode-icons:file-type-zip',
  gz: 'vscode-icons:file-type-zip',
  bz2: 'vscode-icons:file-type-zip',
  xz: 'vscode-icons:file-type-zip',
  tgz: 'vscode-icons:file-type-zip',

  // 设计文件
  psd: 'vscode-icons:file-type-photoshop',
  ai: 'vscode-icons:file-type-ai',
  sketch: 'vscode-icons:file-type-sketch',

  // 字体
  ttf: 'vscode-icons:file-type-font',
  otf: 'vscode-icons:file-type-font',
  woff: 'vscode-icons:file-type-font',
  woff2: 'vscode-icons:file-type-font',
  eot: 'vscode-icons:file-type-font',

  // Docker / Git
  dockerfile: 'vscode-icons:file-type-docker',
  gitignore: 'vscode-icons:file-type-git',

  // 其他
  tex: 'vscode-icons:file-type-tex',
  assembly: 'vscode-icons:file-type-assembly',
};

/**
 * 基于 vscode-icons 的文件类型图标组件
 * 根据文件扩展名自动渲染对应的 VS Code 风格彩色图标
 */
const FileTypeIcon: React.FC<FileTypeIconProps> = ({ fileName, size = 20 }) => {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  const iconName = extensionIconMap[ext] || 'vscode-icons:default-file';

  return (
    <Icon
      icon={iconName}
      width={size}
      height={size}
      style={{ flexShrink: 0 }}
    />
  );
};

export default React.memo(FileTypeIcon);
